import { useState, useMemo, useEffect } from 'react';
import { apiService } from '@vsat/shared';
import { Link, useNavigate } from 'react-router-dom';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../utils/siteConfig';
import { StructuredData } from '../components/StructuredData';
import { MAJOR_CATEGORIES, generateSlug } from '../data/majorsData';

import {
  Search,
  Filter,
  GraduationCap,
  TrendingUp,
  BookOpen,
  Sparkles,
  ChevronRight,
  Briefcase,
  Brain,
  Building2,
  Lightbulb,
  BarChart3,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';

// Map groupCode → Holland RIASEC metadata
const GROUP_META: Record<string, { code: string; name: string; category: string }> = {
  group1: { code: 'R', name: 'Kỹ thuật', category: 'Kỹ thuật & Công nghệ' },
  group2: { code: 'I', name: 'Nghiên cứu', category: 'Khoa học & Nghiên cứu' },
  group3: { code: 'A', name: 'Nghệ thuật', category: 'Nghệ thuật & Sáng tạo' },
  group4: { code: 'S', name: 'Xã hội', category: 'Xã hội & Giáo dục' },
  group5: { code: 'E', name: 'Quản lý', category: 'Kinh tế & Quản lý' },
  group6: { code: 'C', name: 'Nghiệp vụ', category: 'Tài chính & Nghiệp vụ' },
};

export default function MajorsListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [allMajors, setAllMajors] = useState<
    Array<{
      name: string;
      slug: string;
      riasec: string;
      category: string;
      groupName: string;
      subjects: Array<{ subjectName: string }>;
      threshold: number | null;
    }>
  >([]);

  useEffect(() => {
    const fetchMajors = async () => {
      try {
        // Single source of truth: UniversityMajor table (same data as Phase 2 CAT Profile)
        const groups = await apiService.getPublicMajors();
        const flat = groups.flatMap(group => {
          const meta = GROUP_META[group.groupCode];
          if (!meta) return [];
          return group.majors.map(major => ({
            name: major.name,
            slug: generateSlug(major.name),
            riasec: meta.code,
            category: meta.category,
            groupName: meta.name,
            subjects: major.subjects,
            threshold: major.threshold,
          }));
        });
        setAllMajors(flat);
      } catch (error) {
        console.error('Failed to fetch majors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMajors();
  }, []);

  // Filter majors based on search and category
  const filteredMajors = useMemo(() => {
    let filtered = allMajors;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(m => m.riasec === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        m => m.name.toLowerCase().includes(query) || m.category.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allMajors, searchQuery, selectedCategory]);

  // Calculate category statistics
  const categoryStats = useMemo(() => {
    return MAJOR_CATEGORIES.map(cat => ({
      ...cat,
      count: allMajors.filter(m => m.riasec === cat.id).length,
    }));
  }, [allMajors]);

  const handleMajorClick = (slug: string) => {
    navigate(`/kham-pha-nghe-nghiep?career=${slug}`);
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-background-dark text-gray-900 dark:text-white font-body flex flex-col'>
      <SEOHead
        title='Danh sách ngành học đại học Việt Nam | Hướng nghiệp CAT'
        description='Khám phá 100+ ngành học đại học tại Việt Nam. Tìm hiểu về lương, cơ hội việc làm, khối thi và nhóm RIASEC để chọn ngành phù hợp với bạn.'
        keywords='ngành học đại học, chọn ngành đại học, ngành học phù hợp, RIASEC, khối thi, tuyển sinh đại học, ngành hot, học gì ra trường dễ xin việc'
        canonicalUrl={getCanonicalUrl('/nganh-hoc')}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Danh sách ngành học', url: getCanonicalUrl('/nganh-hoc') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12 lg:pt-24'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6'>
          {/* Hero Section - Card-based like detail page */}
          <div className='bg-white dark:bg-card-dark rounded-xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex flex-col md:flex-row gap-6 md:items-start'>
              {/* Icon */}
              <div className='shrink-0'>
                <div className='h-28 w-28 md:h-36 md:w-36 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-800/30 flex items-center justify-center border border-blue-200 dark:border-blue-800 text-primary'>
                  <GraduationCap className='w-14 h-14 md:w-16 md:h-16' />
                </div>
              </div>

              {/* Content */}
              <div className='flex flex-col flex-1 gap-4'>
                <div>
                  <div className='flex items-center gap-2 mb-2 flex-wrap'>
                    <span className='bg-blue-100 dark:bg-blue-900/50 text-primary text-xs font-bold px-2 py-1 rounded uppercase tracking-wider'>
                      Danh sách ngành học
                    </span>
                    <span className='bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-bold px-2 py-1 rounded flex items-center gap-1'>
                      <Sparkles className='w-3 h-3' />
                      {allMajors.length > 0 ? `${allMajors.length}+` : '100+'} Ngành
                    </span>
                  </div>
                  <h1 className='text-2xl md:text-3xl font-bold leading-tight mb-2 text-gray-900 dark:text-white'>
                    Khám phá Ngành học phù hợp với bạn
                  </h1>
                  <p className='text-gray-600 dark:text-gray-400 text-base md:text-lg leading-relaxed'>
                    Tìm hiểu chi tiết về các ngành học từ bài đánh giá CAT. Khám phá cơ hội nghề
                    nghiệp, lộ trình học tập và các trường đại học hàng đầu.
                  </p>
                </div>

                {/* Quick Stats - matching detail page pattern */}
                <div className='flex flex-wrap gap-4 md:gap-8 mt-2 pt-4 border-t border-gray-100 dark:border-gray-800'>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-blue-50 dark:bg-blue-900/20 rounded-full text-blue-600'>
                      <GraduationCap className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Tổng ngành
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        {allMajors.length} ngành học
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-purple-50 dark:bg-purple-900/20 rounded-full text-purple-600'>
                      <Brain className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Nhóm RIASEC
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        6 nhóm nghề nghiệp
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='p-2 bg-orange-50 dark:bg-orange-900/20 rounded-full text-orange-600'>
                      <Building2 className='w-5 h-5' />
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold'>
                        Trường ĐH
                      </span>
                      <span className='text-sm font-bold text-gray-900 dark:text-white'>
                        100+ trường liên kết
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Layout Grid */}
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 items-start'>
            {/* Main Content - 8 columns */}
            <div className='lg:col-span-8 flex flex-col gap-6'>
              {/* Search and Filter Section */}
              <section className='bg-white dark:bg-card-dark rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-3 mb-5'>
                  <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-primary'>
                    <Search className='w-5 h-5' />
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                    Tìm kiếm & Lọc ngành học
                  </h3>
                </div>

                <div className='flex flex-col gap-4'>
                  {/* Search */}
                  <div className='relative'>
                    <Search className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                    <input
                      type='text'
                      placeholder='Tìm kiếm ngành học...'
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className='w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all'
                    />
                  </div>

                  {/* Category Filter */}
                  <div className='flex gap-2 overflow-x-auto pb-2'>
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-4 py-2.5 rounded-lg font-medium whitespace-nowrap transition-all text-sm ${
                        selectedCategory === 'all'
                          ? 'bg-primary text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'
                      }`}
                    >
                      Tất cả ({allMajors.length})
                    </button>
                    {MAJOR_CATEGORIES.map(cat => {
                      const colorClasses: Record<string, string> = {
                        orange:
                          'border-orange-400 text-orange-600 bg-orange-50 dark:bg-orange-900/20',
                        blue: 'border-blue-400 text-blue-600 bg-blue-50 dark:bg-blue-900/20',
                        purple:
                          'border-purple-400 text-purple-600 bg-purple-50 dark:bg-purple-900/20',
                        green: 'border-green-400 text-green-600 bg-green-50 dark:bg-green-900/20',
                        red: 'border-red-400 text-red-600 bg-red-50 dark:bg-red-900/20',
                        gray: 'border-gray-400 text-gray-600 bg-gray-50 dark:bg-gray-800',
                      };
                      const catCount = categoryStats.find(s => s.id === cat.id)?.count || 0;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`px-4 py-2.5 rounded-lg font-medium whitespace-nowrap border-2 transition-all text-sm ${
                            selectedCategory === cat.id
                              ? colorClasses[cat.color]
                              : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'
                          }`}
                        >
                          {cat.hollandCode} - {cat.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Results Count */}
              <div className='flex items-center justify-between px-1'>
                <p className='text-gray-600 dark:text-gray-400'>
                  Hiển thị{' '}
                  <span className='font-bold text-gray-900 dark:text-white'>
                    {filteredMajors.length}
                  </span>{' '}
                  ngành học
                </p>
                {selectedCategory !== 'all' && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    className='text-primary hover:underline font-medium text-sm'
                  >
                    Xóa bộ lọc
                  </button>
                )}
              </div>

              {/* Majors Grid */}
              {loading ? (
                <div className='flex items-center justify-center py-20'>
                  <div className='animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent'></div>
                </div>
              ) : allMajors.length === 0 ? (
                <div className='bg-white dark:bg-card-dark rounded-xl p-10 text-center border border-gray-200 dark:border-gray-700'>
                  <div className='mb-5'>
                    <Brain className='w-16 h-16 mx-auto text-primary mb-4' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                    🎯 Bạn có biết mình phù hợp với ngành nào?
                  </h3>
                  <p className='text-gray-600 dark:text-gray-400 mb-2 text-base max-w-md mx-auto'>
                    Hơn <span className='font-bold text-primary'>100+ ngành học</span> đang chờ bạn
                    khám phá!
                  </p>
                  <p className='text-gray-500 dark:text-gray-400 mb-6 text-sm max-w-lg mx-auto'>
                    Làm bài test <span className='font-semibold text-primary'>CAT miễn phí</span> để
                    khám phá tính cách, điểm mạnh và nhận gợi ý ngành học phù hợp với bạn.
                  </p>
                  <Link
                    to='/huong-nghiep-cat?screen=instruction'
                    className='inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-colors shadow-sm'
                  >
                    <Brain className='w-5 h-5' />
                    Khám phá ngay - Chỉ 10 phút
                  </Link>
                  <p className='text-xs text-gray-500 dark:text-gray-400 mt-4'>
                    ✨ Đã có hơn <span className='font-semibold'>50,000</span> học sinh tin dùng
                  </p>
                </div>
              ) : filteredMajors.length === 0 ? (
                <div className='bg-white dark:bg-card-dark rounded-xl p-12 text-center border border-gray-200 dark:border-gray-700'>
                  <Filter className='w-16 h-16 mx-auto text-gray-300 dark:text-gray-700 mb-4' />
                  <p className='text-gray-500 dark:text-gray-400 text-lg'>
                    Không tìm thấy ngành học phù hợp
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    className='mt-4 text-primary hover:underline font-medium'
                  >
                    Xóa bộ lọc
                  </button>
                </div>
              ) : (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {filteredMajors.map(major => {
                    const isHot =
                      major.name.includes('Công nghệ') ||
                      major.name.includes('AI') ||
                      major.name.includes('Fintech');
                    return (
                      <div
                        key={major.slug}
                        onClick={() => handleMajorClick(major.slug)}
                        className='group bg-white dark:bg-card-dark rounded-xl p-5 border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer'
                      >
                        {/* Header */}
                        <div className='flex items-start justify-between mb-3'>
                          <div className='flex-1'>
                            <h3 className='font-bold text-base text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2'>
                              {major.name}
                            </h3>
                            <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                              {major.category}
                            </p>
                          </div>
                          {isHot && (
                            <span className='bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 text-xs font-bold px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap ml-2'>
                              <TrendingUp className='w-3 h-3' />
                              Hot
                            </span>
                          )}
                        </div>

                        {/* Group + Subjects */}
                        <div className='space-y-2 mb-3'>
                          <div className='flex items-center gap-2 text-sm'>
                            <GraduationCap className='w-4 h-4 text-blue-600' />
                            <span className='text-gray-700 dark:text-gray-300'>
                              Nhóm: {major.groupName}
                            </span>
                          </div>
                          {major.subjects && major.subjects.length > 0 && (
                            <div className='flex flex-wrap gap-1'>
                              {major.subjects.map((s, i) => (
                                <span
                                  key={i}
                                  className='text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                                >
                                  {s.subjectName}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* RIASEC Badge & Action */}
                        <div className='pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between'>
                          <span className='inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded'>
                            <Brain className='w-3 h-3' />
                            {major.riasec}
                          </span>
                          <div className='flex items-center gap-1 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity'>
                            <span>Xem chi tiết</span>
                            <ChevronRight className='w-4 h-4' />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sidebar - 4 columns */}
            <div className='lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-24 h-fit'>
              {/* CAT CTA Card */}
              <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-lg text-white'>
                <div className='flex items-start justify-between mb-4'>
                  <h3 className='text-base font-bold text-white'>Chưa biết ngành phù hợp?</h3>
                  <Brain className='w-5 h-5 text-white/80' />
                </div>
                <p className='text-sm text-blue-100 mb-4 leading-relaxed'>
                  Làm bài đánh giá CAT để khám phá nhóm tính cách và nhận gợi ý ngành học phù hợp
                  nhất với bạn.
                </p>
                <Link
                  to='/huong-nghiep-cat?screen=instruction'
                  className='w-full py-2.5 bg-white text-primary text-sm font-bold rounded-lg shadow-sm hover:bg-blue-50 transition-colors flex items-center justify-center gap-2'
                >
                  <Brain className='w-4 h-4' />
                  Làm bài CAT ngay
                </Link>
              </div>

              {/* Statistics Card */}
              <div className='bg-white dark:bg-card-dark rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-3 mb-4'>
                  <div className='p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-purple-600'>
                    <BarChart3 className='w-5 h-5' />
                  </div>
                  <h3 className='text-base font-bold text-gray-900 dark:text-white'>
                    Thống kê theo nhóm
                  </h3>
                </div>
                <div className='space-y-3'>
                  {categoryStats.map(cat => {
                    const colorBg: Record<string, string> = {
                      orange: 'bg-orange-500',
                      blue: 'bg-blue-500',
                      purple: 'bg-purple-500',
                      green: 'bg-green-500',
                      red: 'bg-red-500',
                      gray: 'bg-gray-500',
                    };
                    const percentage = Math.round((cat.count / allMajors.length) * 100);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`w-full text-left p-2 rounded-lg transition-colors ${selectedCategory === cat.id ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                      >
                        <div className='flex justify-between items-center mb-1'>
                          <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                            {cat.hollandCode} - {cat.name}
                          </span>
                          <span className='text-xs font-bold text-gray-500'>{cat.count}</span>
                        </div>
                        <div className='h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden'>
                          <div
                            className={`h-full ${colorBg[cat.color]} rounded-full transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tips Card */}
              <div className='bg-amber-50 dark:bg-amber-900/10 rounded-xl p-5 border border-amber-100 dark:border-amber-900/30'>
                <div className='flex items-center gap-3 mb-4'>
                  <div className='p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg text-amber-600'>
                    <Lightbulb className='w-5 h-5' />
                  </div>
                  <h3 className='text-base font-bold text-gray-900 dark:text-white'>
                    Mẹo tìm kiếm
                  </h3>
                </div>
                <ul className='space-y-2 text-sm text-gray-700 dark:text-gray-300'>
                  <li className='flex gap-2'>
                    <span className='text-amber-500'>•</span>
                    <span>Tìm theo tên ngành hoặc từ khóa liên quan</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-amber-500'>•</span>
                    <span>Lọc theo nhóm RIASEC để thu hẹp kết quả</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-amber-500'>•</span>
                    <span>Click vào ngành để xem chi tiết thông tin</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.main>

      {/* Mobile Sticky CTA */}
      <div className='lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-surface-dark border-t border-gray-200 dark:border-gray-700 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'>
        <div className='flex items-center gap-3'>
          <div className='flex-1'>
            <div className='flex items-center gap-2 mb-1'>
              <Brain className='w-4 h-4 text-primary' />
              <h3 className='text-sm font-bold text-gray-900 dark:text-white'>
                Chưa biết ngành phù hợp?
              </h3>
            </div>
            <p className='text-xs text-gray-500 dark:text-gray-400 line-clamp-1'>
              Làm bài CAT để nhận gợi ý ngành học.
            </p>
          </div>
          <Link
            to='/huong-nghiep-cat?screen=instruction'
            className='px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold rounded-lg shadow-md transition-all whitespace-nowrap flex items-center gap-1.5'
          >
            Làm ngay
            <ArrowRight className='w-3.5 h-3.5' />
          </Link>
        </div>
      </div>

      <UnifiedFooter />
    </div>
  );
}
